import { isEqual, lowerCase, startCase } from 'lodash-es'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { LuListFilter, LuX } from 'react-icons/lu'

import { CheckboxInput } from '@/components/CheckboxInput'

import { TaskStatus } from '../CreateTaskModal'
import { OpeningFilterPopout } from '../ExecutionBoardFilters/ExecutionBoardFilters'
import styles from '../ExecutionBoardFilters/ExecutionBoardFilters.module.css'

export const StatusFilterPopout = ({
  setFilteringStatuses,
  currentSelectedStatuses,
  openingFilterPopout,
  onOpenFilterPopout,
}: {
  setFilteringStatuses: (statuses: TaskStatus[] | null) => void
  currentSelectedStatuses?: TaskStatus[]
  openingFilterPopout: OpeningFilterPopout | null
  onOpenFilterPopout: (popout: OpeningFilterPopout) => void
}) => {
  const [selectedStatuses, setSelectedStatuses] = useState<TaskStatus[]>([])

  const statusFilterButtonRef = useRef<HTMLDivElement>(null)

  const dialogRef = useRef<HTMLDialogElement>(null)

  const [popoverPosition, setPopoverPosition] = useState(
    statusFilterButtonRef.current?.getBoundingClientRect() ?? { bottom: 0, right: 0 },
  )

  const showPopover = (e: React.MouseEvent) => {
    e.stopPropagation()
    const pos = statusFilterButtonRef.current?.getBoundingClientRect()
    if (pos) {
      setPopoverPosition(pos)
    }
    dialogRef?.current?.showPopover()
    onOpenFilterPopout(OpeningFilterPopout.STATUS)
  }

  const { bottom, right } = popoverPosition

  const handleClose = useCallback(
    (applyClose: boolean = false) => {
      if (dialogRef.current) {
        if (!applyClose && !isEqual(currentSelectedStatuses, selectedStatuses)) {
          setSelectedStatuses(currentSelectedStatuses ?? [])
        }
        dialogRef.current.hidePopover()
      }
    },
    [currentSelectedStatuses, selectedStatuses],
  )

  const handleSelectStatus = useCallback((status: TaskStatus) => {
    setSelectedStatuses((prev) => {
      const isSelected = prev.includes(status)
      if (isSelected) {
        return prev.filter((selectedStatus) => selectedStatus !== status)
      } else {
        return [...prev, status]
      }
    })
  }, [])

  const handleClearAll = useCallback(() => {
    setSelectedStatuses([])
    setFilteringStatuses(null)
  }, [setFilteringStatuses])

  const handleApply = useCallback(() => {
    setFilteringStatuses(selectedStatuses)
    handleClose(true)
  }, [selectedStatuses, setFilteringStatuses, handleClose])

  useEffect(() => {
    if (openingFilterPopout !== OpeningFilterPopout.STATUS) {
      handleClose()
    }
  }, [openingFilterPopout])

  return (
    <div>
      <div
        ref={statusFilterButtonRef}
        className={styles.button}
        data-testid="execution-board-button"
        onClick={showPopover}
      >
        <LuListFilter size="16" />
        Status
      </div>

      <dialog
        ref={dialogRef}
        className={styles.popoutContainer}
        style={{ top: bottom + 10, left: right - 80 }}
        popover="manual"
      >
        <div className={styles.popoutHeader}>
          <div className={styles.headerActions}>
            <button
              className={styles.actionButton}
              onClick={() => {
                handleClose(true)
              }}
            >
              <LuX size={16} />
            </button>
          </div>
        </div>

        <div className={styles.checkboxesWrapper}>
          {Object.keys(TaskStatus).map((key: string) => (
            <CheckboxInput
              key={`status-${key}`}
              checked={selectedStatuses.includes(key as TaskStatus)}
              onChange={() => handleSelectStatus(key as TaskStatus)}
            >
              {startCase(lowerCase(key))}
            </CheckboxInput>
          ))}
        </div>
        <div className={styles.footerActions}>
          <button className={styles.clearAllButton} onClick={handleClearAll}>
            Clear all
          </button>
          <button className={styles.applyButton} onClick={handleApply}>
            Apply
          </button>
        </div>
      </dialog>
    </div>
  )
}
